import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Image as ImageIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  FactCheck as StructuredIcon,
  ShortText as UnstructuredIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon,
  Storage as StorageIcon,
  Restaurant as RestaurantIcon,

} from '@mui/icons-material';
import { FoodDetailDto } from '../../../services/admin-food.service';
import { getImageUrl } from '../../../utils/image-url.util';

interface FoodDetailDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  data: FoodDetailDto | null;
  isLoading: boolean;
  getCategoryLabel: (value: string) => string;
  getMeasurementUnitLabel: (value: string) => string;
}

export const FoodDetailDialog: React.FC<FoodDetailDialogProps> = ({
  open,
  onClose,
  onEdit,
  data,
  isLoading,
  getCategoryLabel,
  getMeasurementUnitLabel
}) => {
  if (!data && !isLoading) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Деталі продукту #{data?.id}
        {data?.isSystem && (
          <Tooltip title="Системний елемент">
            <VerifiedIcon color="primary" />
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography>Завантаження...</Typography>
        ) : data ? (
          <Grid container spacing={3}>
            {/* Header with Image and Basic Info */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Основне зображення</Typography>
                <Avatar
                  variant="rounded"
                  src={data.imageUrl ? getImageUrl(data.imageUrl) : undefined}
                  alt="Food Image"
                  sx={{ width: 150, height: 150, margin: '0 auto', border: '1px solid #eee' }}
                >
                  <ImageIcon sx={{ fontSize: 60 }} />
                </Avatar>
              </Box>

              {data.legacyImg && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Legacy зображення
                    <Tooltip title="Оригінальне зображення зі старої бази">
                      <StorageIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'text-bottom' }} />
                    </Tooltip>
                  </Typography>
                  <Box 
                    component="img"
                    src={data.legacyImg}
                    alt="Legacy Food"
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      objectFit: 'cover', 
                      borderRadius: 1,
                      border: '1px dashed #ccc',
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(data.legacyImg!, '_blank')}
                    title="Клікніть, щоб відкрити оригінал"
                  />
                </Box>
              )}
              
              <Stack spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip 
                  icon={data.isStructured ? <StructuredIcon /> : <UnstructuredIcon />} 
                  label={data.isStructured ? "Структурований" : "Неструктурований"} 
                  color={data.isStructured ? "success" : "default"} 
                  variant="outlined"
                />
                
                <Chip 
                  label={getCategoryLabel(data.category)} 
                  color="primary" 
                  size="small" 
                />

                {data.legacyId && (
                  <Tooltip title={`Імпортовано з легасі ${data.legacyEntity === 'dish' ? 'страв' : 'продуктів'} (ID: ${data.legacyId})`}>
                    <Chip
                      icon={data.legacyEntity === 'dish' ? <RestaurantIcon /> : <StorageIcon />}
                      label={`Legacy ID: ${data.legacyId}`}
                      variant="outlined"
                      size="small"
                      color="warning"
                    />
                  </Tooltip>
                )}
              </Stack>

              {data.externalLink && (
                <Button 
                  startIcon={<LinkIcon />} 
                  href={data.externalLink} 
                  target="_blank" 
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Зовнішнє посилання
                </Button>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              {/* Translations Table */}
              <Typography variant="h6" gutterBottom>Назви (Переклади)</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Мова</TableCell>
                      <TableCell>Назва</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(data.nameI18n).map(([lang, name]) => (
                      <TableRow key={lang}>
                        <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px' }}>{lang}</TableCell>
                        <TableCell>{name || <Typography variant="caption" color="text.secondary">Немає перекладу</Typography>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Nutrition Info - Moved UP */}
              <Typography variant="h6" gutterBottom>Харчова цінність (на 100г)</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">Ккал</Typography>
                    <Typography variant="body1" fontWeight="bold">{data.caloriesPer100g ?? '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">Білки</Typography>
                    <Typography variant="body1" fontWeight="bold">{data.proteinPer100g ?? '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">Жири</Typography>
                    <Typography variant="body1" fontWeight="bold">{data.fatPer100g ?? '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">Вугл.</Typography>
                    <Typography variant="body1" fontWeight="bold">{data.carbsPer100g ?? '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">Клітч.</Typography>
                    <Typography variant="body1" fontWeight="bold">{data.fiberPer100g ?? '-'}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Details / Description Table - Moved DOWN */}
              {data.detailsI18n && Object.keys(data.detailsI18n).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Опис / Інструкція</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Мова</TableCell>
                          <TableCell>Опис</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(data.detailsI18n).map(([lang, text]) => (
                          <TableRow key={lang}>
                            <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px', verticalAlign: 'top' }}>{lang}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                              {text || <Typography variant="caption" color="text.secondary">Порожньо</Typography>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Measurement Info */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Одиниця виміру</Typography>
                  <Typography variant="body1">{getMeasurementUnitLabel(data.measurementUnit || '')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Вага 1 шт (г)</Typography>
                  <Typography variant="body1">{data.gramsPerUnit ?? '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Дефолтна порція</Typography>
                  <Typography variant="body1">{data.defaultMeasurementValue ?? '-'}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                {/* Author Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> Автор
                  </Typography>
                  {data.creator ? (
                    <Box>
                      <Typography variant="body1" fontWeight="bold">{data.creator.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">{data.creator.email}</Typography>
                      <Typography variant="caption" color="text.secondary">ID: {data.creator.id}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" color="text.secondary">Система</Typography>
                      {data.isSystem && <VerifiedIcon color="primary" fontSize="small" />}
                    </Box>
                  )}
                </Grid>

                {/* Statistics */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon /> Статистика
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Використано разів</Typography>
                      <Typography variant="h5" color="primary">{data.usageCount}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Створено</Typography>
                      <Typography variant="body2">{new Date(data.createdAt).toLocaleString()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Оновлено</Typography>
                      <Typography variant="body2">{new Date(data.updatedAt).toLocaleString()}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Typography color="error">Не вдалося завантажити дані</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onEdit} startIcon={<EditIcon />} color="primary" variant="contained">
          Редагувати
        </Button>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};
